import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import { formatCurrency } from '../../utils/currencyUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

// A small radio or checkbox is 38px tall and a body2 line 20px: starting the text 9px down
// centres its first line on the control, so labels that wrap still line up.
const CONTROL_LABEL_SX = { alignItems: 'flex-start', mr: 0 };
const FIRST_LINE_OFFSET = '9px';

/** The payment option in force: the deposit route unless the customer chose to pay in full. */
export const resolveActivePaymentOption = (quote, payInFull) => {
  if (!quote) {
    return null;
  }

  return !payInFull && quote.minimum ? quote.minimum : quote.full;
};

const AmountRow = ({ label, value, strong }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={strong ? 700 : 600} sx={{ color: strong ? BRAND.accentText : BRAND.text }}>{value}</Typography>
  </Box>
);

/**
 * What the customer pays online, and their agreement to the terms. The page keeps only the
 * choice and a short checkbox; the full terms open in a dialog. They are still worded by the
 * server from the same numbers it stores, which is what makes them usable in a card dispute.
 */
const BookingPaymentTerms = ({
  quote,
  loading,
  failed,
  payInFull,
  onPayInFullChange,
  accepted,
  onAcceptedChange,
  currencyCode,
}) => {
  const { t } = useTranslation();
  const [termsOpen, setTermsOpen] = useState(false);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} sx={{ color: BRAND.accent }} />
        <Typography variant="body2" color="text.secondary">{t('customer.appointments.booking.policy.loading')}</Typography>
      </Box>
    );
  }

  if (failed || !quote) {
    return failed ? (
      <Alert severity="warning" sx={{ borderRadius: 1.5 }}>{t('customer.appointments.booking.policy.error')}</Alert>
    ) : null;
  }

  const option = resolveActivePaymentOption(quote, payInFull);
  const currency = quote.currency || currencyCode;
  const money = (amount) => formatCurrency(amount, currency);
  const requiresAcceptance = option.nonRefundableAmount > 0;
  const offersChoice = quote.allowsPayInFull && quote.minimum;

  // The link sits inside the checkbox label: opening the terms must not also tick the box.
  const openTerms = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setTermsOpen(true);
  };

  const termsLink = (
    <Link component="button" type="button" onClick={openTerms} sx={{ color: BRAND.accentText, fontWeight: 700, verticalAlign: 'baseline' }}>
      {t('customer.appointments.booking.policy.termsLink')}
    </Link>
  );

  const radio = <Radio size="small" sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText } }} />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {offersChoice ? (
        <RadioGroup
          value={payInFull ? 'full' : 'deposit'}
          onChange={(event) => onPayInFullChange(event.target.value === 'full')}
        >
          <FormControlLabel
            value="deposit"
            control={radio}
            sx={CONTROL_LABEL_SX}
            label={(
              <Box sx={{ pt: FIRST_LINE_OFFSET }}>
                <Typography variant="body2" fontWeight={payInFull ? 400 : 600}>
                  {t('customer.appointments.booking.policy.payDepositNow', { now: money(quote.minimum.onlineAmount) })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('customer.appointments.booking.policy.restAtStore', { later: money(quote.minimum.inStoreAmount) })}
                </Typography>
              </Box>
            )}
          />
          <FormControlLabel
            value="full"
            control={radio}
            sx={CONTROL_LABEL_SX}
            label={(
              <Typography variant="body2" fontWeight={payInFull ? 600 : 400} sx={{ pt: FIRST_LINE_OFFSET }}>
                {t('customer.appointments.booking.policy.payFullOption', { now: money(quote.full.onlineAmount) })}
              </Typography>
            )}
          />
        </RadioGroup>
      ) : (
        <Box>
          <AmountRow label={t('customer.appointments.booking.policy.payNow')} value={money(option.onlineAmount)} strong />
          {option.inStoreAmount > 0 ? (
            <AmountRow label={t('customer.appointments.booking.policy.payAtStore')} value={money(option.inStoreAmount)} />
          ) : null}
        </Box>
      )}

      {requiresAcceptance ? (
        <FormControlLabel
          sx={CONTROL_LABEL_SX}
          control={(
            <Checkbox
              size="small"
              checked={accepted}
              onChange={(event) => onAcceptedChange(event.target.checked)}
              inputProps={{ 'aria-label': t('customer.appointments.booking.policy.acceptAria') }}
              sx={{ color: BRAND.accentText, '&.Mui-checked': { color: BRAND.accentText } }}
            />
          )}
          label={(
            <Typography variant="body2" sx={{ pt: FIRST_LINE_OFFSET }}>
              {t('customer.appointments.booking.policy.acceptLead')}
              {' '}
              {termsLink}
            </Typography>
          )}
        />
      ) : (
        <Typography variant="body2">{termsLink}</Typography>
      )}

      <ScrollableDialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        maxWidth="sm"
        showCloseButton
        title={t('customer.appointments.booking.policy.termsTitle')}
        actions={requiresAcceptance ? (
          <>
            <Button color="inherit" onClick={() => setTermsOpen(false)}>{t('common.close')}</Button>
            <Button
              variant="contained"
              onClick={() => {
                onAcceptedChange(true);
                setTermsOpen(false);
              }}
              sx={{ bgcolor: BRAND.accent, color: BRAND.onAccent, '&:hover': { bgcolor: BRAND.accentDark } }}
            >
              {t('customer.appointments.booking.policy.acceptButton')}
            </Button>
          </>
        ) : (
          <Button color="inherit" onClick={() => setTermsOpen(false)}>{t('common.close')}</Button>
        )}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ borderRadius: 1.5, backgroundColor: brandAlpha('accent', 0.06), p: 1.5 }}>
            <AmountRow label={t('customer.appointments.booking.policy.payNow')} value={money(option.onlineAmount)} strong />
            {option.inStoreAmount > 0 ? (
              <AmountRow label={t('customer.appointments.booking.policy.payAtStore')} value={money(option.inStoreAmount)} />
            ) : null}
          </Box>
          {quote.depositAmount > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t('customer.appointments.booking.policy.depositNotice', { amount: money(Math.min(quote.depositAmount, option.onlineAmount)) })}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ color: BRAND.text, whiteSpace: 'pre-line' }} data-testid="booking-policy-text">
            {option.policyText}
          </Typography>
        </Box>
      </ScrollableDialog>
    </Box>
  );
};

export default BookingPaymentTerms;
